import * as React from "react"
import { isOrderScriptSent } from "@nexus/contracts"
import { Button, Icon, cn } from "@nexus/ui"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import type { ScriptDto } from "@/lib/queries"
import type { SentScriptsMap } from "@/features/marketplace/types/marketplace.types"
import { exportSelectedScriptsXlsx } from "@/features/opportunities/api/opportunities.api.js"
import { triggerBlobDownload } from "@/features/opportunities/lib/triggerBlobDownload.js"
import { getScriptDisplayTitle, isUsableScriptText } from "@/features/opportunities/lib/script-display-title"
import { trackScriptCopied, trackScriptCsvDownloaded } from "@/lib/analytics-events"

export interface ScriptsBulkActionBarProps {
  setId: string
  selectedCount: number
  selectedScripts: ScriptDto[]
  sentScriptsMap?: SentScriptsMap
  onClearSelection: () => void
  onAiEditSelected: (ids: string[]) => void
  onSendToVendor?: (ids: string[]) => void
}

export function formatForCopy(scripts: ScriptDto[], untitled: string): string {
  return scripts
    .map((s) => {
      const parts: string[] = []
      parts.push(getScriptDisplayTitle(s, untitled))
      if (s.adDescription) parts.push(s.adDescription)
      const seq = s.scenes.length > 0 ? s.scenes.map((sc) => sc.scene_name).join(" → ") : s.sequence
      if (seq) parts.push(`Sequence: ${seq}`)
      if (isUsableScriptText(s.concept)) parts.push(`Concept: ${s.concept!.trim()}`)
      if (s.presenter) parts.push(`Presenter: ${s.presenter}`)
      if (s.product) parts.push(`Product: ${s.product}`)
      return parts.join("\n")
    })
    .join("\n\n---\n\n")
}

export function ScriptsBulkActionBar({
  setId,
  selectedCount,
  selectedScripts,
  sentScriptsMap = {},
  onClearSelection,
  onAiEditSelected,
  onSendToVendor,
}: ScriptsBulkActionBarProps) {
  const { t } = useTranslation()
  const [copying, setCopying] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)
  const unsentSelectedCount = selectedScripts.filter(
    (script) => !isOrderScriptSent(sentScriptsMap[script.id]),
  ).length

  if (selectedCount === 0) return null

  async function handleDownloadXlsx() {
    setExporting(true)
    try {
      const { blob, rowCount } = await exportSelectedScriptsXlsx({
        setId,
        scriptIds: selectedScripts.map((s) => s.id),
      })
      const filename = `selected-scripts-${new Date().toISOString().slice(0, 10)}.xlsx`
      triggerBlobDownload(blob, filename)
      toast.success(t("opportunities.scripts.bulk.downloadXlsxToast", { count: rowCount }))

      const scriptIds = selectedScripts.map((s) => s.id)
      trackScriptCsvDownloaded({
        script_ids: scriptIds,
        script_count: scriptIds.length,
        opportunity_set_id: setId,
        export_source: 'selection_tray',
      })
    } catch {
      toast.error(t("opportunities.scripts.bulk.downloadXlsxError"))
    } finally {
      setExporting(false)
    }
  }

  async function handleCopySelected() {
    const text = formatForCopy(
      selectedScripts,
      t("opportunities.scripts.untitledTitle", "Untitled script"),
    )
    await navigator.clipboard.writeText(text)
    setCopying(true)
    toast.success(t("opportunities.scripts.bulk.copySelectedToast", { count: selectedCount }))
    setTimeout(() => setCopying(false), 2000)

    trackScriptCopied({
      script_ids: selectedScripts.map((s) => s.id),
      script_count: selectedCount,
      copy_mode: 'bulk',
      copy_format: 'markdown',
      opportunity_set_id: setId,
    })
  }

  return (
    <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex max-w-[calc(100vw-2rem)] flex-wrap items-center gap-2 rounded-md border border-border bg-background px-4 py-2.5 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] sm:gap-3 sm:px-5 dark:bg-muted">

        {/* Selection info */}
        <div className="flex items-center gap-2 border-r border-border/60 pr-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-medium tabular-nums text-primary-foreground">
            {selectedCount}
          </span>
          <span className="text-sm font-medium text-foreground whitespace-nowrap">
            {t("opportunities.scripts.bulk.selected", { count: selectedCount })}
          </span>
          <button
            type="button"
            onClick={onClearSelection}
            className="ml-0.5 text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline whitespace-nowrap"
          >
            {t("opportunities.scripts.bulk.clearSelection")}
          </button>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void handleDownloadXlsx() }}
            disabled={exporting}
            aria-label={t("opportunities.scripts.bulk.downloadXlsx")}
            data-testid="bulk-download-xlsx"
            className={cn("text-xs", exporting && "tabular-nums")}
          >
            {exporting ? (
              <>
                <Icon name="loader" size="xs" className="animate-spin shrink-0" />
                <span>{t("common.downloading")}</span>
              </>
            ) : (
              <>
                <Icon name="download" size="xs" className="shrink-0" />
                <span>{t("opportunities.scripts.bulk.downloadXlsx")}</span>
              </>
            )}
          </Button>

          <Button
            variant="outline"
            size="sm"
            iconStart={copying ? "check" : "copy"}
            onClick={() => { void handleCopySelected() }}
            aria-label={t("opportunities.scripts.bulk.copySelected")}
            data-testid="bulk-copy-selected"
            className="text-xs"
          >
            {t("opportunities.scripts.bulk.copySelected")}
          </Button>

          <Button
            size="sm"
            onClick={() => onAiEditSelected(selectedScripts.map((s) => s.id))}
            aria-label={t("opportunities.scripts.bulk.aiEditSelected")}
            data-testid="bulk-ai-edit"
            className="gap-1.5 bg-[var(--ai-fill-medium)] text-xs font-normal text-[var(--ai-text)] shadow-none hover:bg-[var(--ai-fill-chip)]"
          >
            <Icon name="sparkles" size="xs" className="text-current" />
            {t("opportunities.scripts.bulk.aiEditSelected")}
          </Button>

          {onSendToVendor && (
            <Button
              variant="outline"
              size="sm"
              iconStart="upload"
              onClick={() => onSendToVendor(selectedScripts.map((s) => s.id))}
              disabled={unsentSelectedCount === 0}
              title={
                unsentSelectedCount === 0
                  ? t("opportunities.scripts.sendDialog.allAlreadySent")
                  : undefined
              }
              aria-label={t("opportunities.scripts.bulk.sendToVendor", "Send to production partner")}
              data-testid="bulk-send-to-vendor"
              className="text-xs"
            >
              {t("opportunities.scripts.bulk.sendToVendor", "Send to production partner")}
            </Button>
          )}

        </div>

        {/* Close */}
        <div className="ml-1 border-l border-border/60 pl-2">
          <button
            type="button"
            onClick={onClearSelection}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t("opportunities.scripts.bulk.clearSelection")}
          >
            <Icon name="x" size="xs" />
          </button>
        </div>
      </div>
    </div>
  )
}
